import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { ModalFrame } from "./modal-frame"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { quoteSchema } from "@/lib/form-validation"
import { CheckCircle2, Loader2, Sparkles } from "lucide-react"

interface OfferModalProps {
  isOpen: boolean
  onClose: () => void
  onDismiss: () => void
  suppressAll: (reason?: string) => void
}

interface OfferFormState {
  fullName: string
  workEmail: string
  company: string
  projectGoal: string
}

const defaultFormState: OfferFormState = {
  fullName: "",
  workEmail: "",
  company: "",
  projectGoal: "",
}

export function OfferModal({ isOpen, onClose, onDismiss, suppressAll }: OfferModalProps) {
  const [formState, setFormState] = useState<OfferFormState>(defaultFormState)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFieldChange = (field: keyof OfferFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(previous => ({ ...previous, [field]: event.target.value }))
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    const validation = quoteSchema.safeParse({
      email: formState.workEmail,
      fullName: formState.fullName,
      company: formState.company,
      projectGoal: formState.projectGoal,
    })

    if (!validation.success) {
      toast.error(validation.error.errors[0].message)
      return
    }

    const validData = validation.data

    setIsSubmitting(true)
    try {
      const { data: response, error } = await supabase.functions.invoke('submit-form', {
        body: {
          email: validData.email,
          type: 'quote',
          data: {
            email: validData.email,
            estimated_price: 1499,
            status: "new",
            quote_details: {
              source: "clawship-offer-modal",
              fullName: validData.fullName,
              company: validData.company,
              projectGoal: validData.projectGoal,
              package: "Clawbiz Setup & Integration",
              capturedAt: new Date().toISOString(),
            },
          }
        }
      })

      if (error || !response?.success) {
        toast.error(response?.error || "We could not save your request. Please try again.")
        setIsSubmitting(false)
        return
      }

      toast.success("Request received! We'll reach out within a few hours.")
      setFormState(defaultFormState)
      onClose()
      onDismiss()
    } catch (error) {
      toast.error("Unexpected error. Please refresh and try again.")
      setIsSubmitting(false)
    }
  }

  const handleSkip = () => {
    suppressAll("user-navigation-opt-out")
    onClose()
    onDismiss()
  }

  if (!isOpen) return null

  return (
    <ModalFrame
      type="Offer"
      title="Ready to get Clawbiz running for you?"
      description="You unlocked our priority setup slot. Share your details and we'll send a concrete setup plan, timeline, and onboarding call within hours."
      highlight="Limited slots available this week • Priority access locked in"
    >
      <div className="grid gap-8 md:grid-cols-[1.2fr,1fr]">
        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="space-y-2">
              <label htmlFor="offer-full-name" className="text-sm font-medium text-muted-foreground">
                Your name
              </label>
              <Input
                id="offer-full-name"
                value={formState.fullName}
                onChange={handleFieldChange("fullName")}
                placeholder="Alex Founder"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="offer-work-email" className="text-sm font-medium text-muted-foreground">
                Work email
              </label>
              <Input
                id="offer-work-email"
                type="email"
                value={formState.workEmail}
                onChange={handleFieldChange("workEmail")}
                placeholder="alex@startup.com"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="offer-company" className="text-sm font-medium text-muted-foreground">
                Company or project name
              </label>
              <Input
                id="offer-company"
                value={formState.company}
                onChange={handleFieldChange("company")}
                placeholder="Stealth AI"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="offer-project-goal" className="text-sm font-medium text-muted-foreground">
                What do you want Clawbiz to do for you?
              </label>
              <Textarea
                id="offer-project-goal"
                value={formState.projectGoal}
                onChange={handleFieldChange("projectGoal")}
                placeholder="Which channels, integrations, or workflows matter most? Tell us in 2-3 sentences."
                rows={4}
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full gap-2 rounded-full py-6 text-lg font-semibold shadow-lg shadow-primary/30 transition-all hover:shadow-primary/40"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Claim my priority setup slot
          </Button>
          <button
            type="button"
            onClick={handleSkip}
            className="mx-auto block text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Not right now — hide offers
          </button>
        </form>

        <div className="space-y-6 rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-7">
          <div className="space-y-3">
            <h3 className="font-heading text-xl text-primary">Your setup includes</h3>
            <ul className="space-y-3 text-sm text-primary/80 md:text-base">
              {[
                "Full Clawbiz install, hardening, and deployment on your machine or cloud",
                "Multi-channel wiring — WhatsApp, Telegram, Discord, Slack, and more",
                "Custom agent configuration with your preferred AI models and tools",
                "Ongoing ops support and async updates with walkthroughs",
              ].map(item => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-foreground/10 bg-background/90 p-4 text-sm text-muted-foreground shadow-inner">
            <p className="font-medium text-foreground">Your data. Your rules.</p>
            <p>Everything runs on your infrastructure. We set it up, you own it completely.</p>
          </div>
        </div>
      </div>
    </ModalFrame>
  )
}
